import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import useColorFavorites from './useColorFavorites';

/**
 * A colour swatch that opens presets, the organisation's saved colours, and a
 * field for any colour at all.
 *
 * Its own module rather than living in CustomTableEditor, which is where it
 * started: CustomTableEditor imports InlineCellEditor, so an inline cell's
 * colour control reaching back for the picker would close a circular import.
 *
 * The panel is rendered into document.body rather than next to the swatch.
 * Positioned absolutely inside the editor it was clipped by the section card,
 * which sets overflow: hidden -- the custom-colour row sits at the bottom of
 * the panel, so it was the part that disappeared, and the picker read as
 * offering presets and nothing else. Nothing about a parent's overflow can
 * reach a portalled node.
 */

const PRESET_COLORS = [
  null, '#fef2f2', '#fff7ed', '#fefce8', '#f0fdf4', '#eff6ff', '#faf5ff', '#fdf2f8',
  '#fee2e2', '#ffedd5', '#fef9c3', '#dcfce7', '#dbeafe', '#e9d5ff', '#fce7f9',
  '#fca5a5', '#fdba74', '#fde047', '#86efac', '#93c5fd', '#c084fc', '#f9a8d4',
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899',
  '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#2563eb', '#7c3aed', '#db2777',
  '#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#f3f4f6', '#ffffff',
];

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

const PANEL_W = 170;
const GAP = 4;
const EDGE = 8;

const NONE_BG = 'linear-gradient(135deg, #fff 45%, #ef4444 45%, #ef4444 55%, #fff 55%)';
const swatch = (c, selected, size = 20) => ({
  width: size, height: size, borderRadius: 3,
  border: selected ? '2px solid var(--primary)' : '1px solid var(--border)',
  background: c || NONE_BG,
  cursor: 'pointer', padding: 0,
});

/**
 * @param {boolean} preserveFocus  Keep the document selection while picking.
 *   Needed wherever the colour is applied with document.execCommand, which
 *   acts on whatever is selected: clicking a normal button blurs the
 *   contenteditable first, so the command lands on nothing and silently does
 *   nothing. preventDefault on mousedown stops the focus moving at all.
 */
function ColorPicker({ value, onChange, label, size = 20, preserveFocus = false, allowNone = true }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [hex, setHex] = useState('');
  const { colors: favorites, canEdit, add, remove } = useColorFavorites();

  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const norm = v => (typeof v === 'string' ? v.toLowerCase() : v);
  const isFavorite = !!value && favorites.includes(norm(value));
  const hold = preserveFocus ? (e => { e.preventDefault(); e.stopPropagation(); }) : (e => e.stopPropagation());

  // Anchored to the swatch in viewport coordinates, and flipped above it when
  // the panel would run off the bottom -- a swatch near the foot of a long
  // template is the common case, not the rare one.
  const place = useCallback(() => {
    const b = btnRef.current && btnRef.current.getBoundingClientRect();
    if (!b) return;
    const ph = (panelRef.current && panelRef.current.offsetHeight) || 260;
    const pw = (panelRef.current && panelRef.current.offsetWidth) || PANEL_W;

    let top = b.bottom + GAP;
    if (top + ph > window.innerHeight - EDGE) {
      const above = b.top - GAP - ph;
      top = above >= EDGE ? above : Math.max(EDGE, window.innerHeight - ph - EDGE);
    }
    let left = b.left;
    if (left + pw > window.innerWidth - EDGE) left = Math.max(EDGE, window.innerWidth - pw - EDGE);

    setCoords({ top, left });
  }, []);

  // After the panel exists its real height is known, so the first placement is
  // corrected before paint rather than visibly jumping.
  useLayoutEffect(() => { if (open) place(); }, [open, place, favorites.length]);

  useEffect(() => {
    if (!open) return;
    // Capture, so scrolling an inner pane repositions it too, not just window.
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);

    // CAPTURE, not bubble. The editor is full of onMouseDown handlers that
    // call stopPropagation -- the cell toolbar's colour controls, and this
    // picker's own trigger button -- and React's synthetic system stops the
    // native event with them. A bubble listener on document therefore never
    // fires for exactly the clicks that should dismiss the panel, so it stayed
    // open when you clicked a cell control, and opening a second picker left
    // the first one up as well. Capture runs from document downwards, before
    // any of them, and the contains() checks below still keep clicks inside
    // the panel or on its own trigger from closing it.
    const onDocDown = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown, true);

    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('mousedown', onDocDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  const pick = (c) => { onChange(c); setOpen(false); };

  const onHex = (raw) => {
    setHex(raw);
    const v = raw.trim();
    if (HEX_RE.test(v)) onChange(v.toLowerCase());
  };

  const presets = allowNone ? PRESET_COLORS : PRESET_COLORS.filter(c => c !== null);

  const panel = (
    <div
      ref={panelRef}
      // Marks the panel as part of whatever opened it. InlineCellEditor keeps
      // its toolbar mounted only while focus stays inside its wrapper, and a
      // portalled panel is not inside it -- so focusing the hex field tore the
      // toolbar, and this picker with it, down 50ms later.
      data-colorpicker-panel=""
      onClick={e => e.stopPropagation()}
      onMouseDown={hold}
      style={{
        position: 'fixed',
        top: coords ? coords.top : -9999,
        left: coords ? coords.left : -9999,
        visibility: coords ? 'visible' : 'hidden',
        zIndex: 4000,
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', padding: 6, boxShadow: 'var(--shadow-lg)', width: PANEL_W,
      }}>

      {favorites.length > 0 && (
        <>
          <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                        color: 'var(--text-muted)', marginBottom: 3 }}>Saved</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 20px)', gap: 3, marginBottom: 6 }}>
            {favorites.map(c => (
              <button key={c} type="button" onMouseDown={hold}
                onClick={e => { e.stopPropagation(); pick(c); }}
                // Right-click to remove, so the grid stays a grid of
                // colours rather than every swatch growing a delete button.
                onContextMenu={canEdit ? (e => { e.preventDefault(); e.stopPropagation(); remove(c); }) : undefined}
                style={swatch(c, norm(value) === c)}
                title={canEdit ? `${c} — right-click to remove` : c}
              />
            ))}
          </div>
        </>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 20px)', gap: 3 }}>
        {presets.map((c, i) => (
          <button key={i} type="button" onMouseDown={hold}
            onClick={e => { e.stopPropagation(); pick(c); }}
            style={swatch(c, c === value)}
            title={c || 'None'}
          />
        ))}
      </div>

      {/* Any colour at all. The picker used to offer these presets and
          nothing else, so a brand colour that was not one of them could not
          be set here. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6,
                    paddingTop: 6, borderTop: '1px solid var(--border)' }}>
        <input type="color" value={HEX_RE.test(value || '') ? value : '#000000'}
          onMouseDown={hold}
          onChange={e => { onChange(e.target.value); setHex(e.target.value); }}
          title="Pick any colour"
          style={{ width: 24, height: 22, padding: 0, border: '1px solid var(--border)',
                   borderRadius: 3, background: 'none', cursor: 'pointer' }} />
        <input type="text" value={hex} onChange={e => onHex(e.target.value)}
          onMouseDown={e => e.stopPropagation()}
          placeholder="#24416d" spellCheck={false}
          style={{ flex: 1, minWidth: 0, fontSize: 11, fontFamily: 'monospace', padding: '2px 4px',
                   border: '1px solid var(--border)', borderRadius: 3,
                   background: 'var(--bg-input)', color: 'var(--text)' }} />
        {canEdit && (
          <button type="button" onMouseDown={hold}
            disabled={!value || !HEX_RE.test(value)}
            onClick={e => { e.stopPropagation(); if (isFavorite) remove(value); else add(value); }}
            title={!value || !HEX_RE.test(value) ? 'Choose a colour first'
                   : isFavorite ? 'Remove from saved colours' : 'Save this colour for everyone'}
            style={{ background: 'none', border: 'none', cursor: value ? 'pointer' : 'default',
                     fontSize: 14, lineHeight: 1, padding: 0,
                     color: isFavorite ? 'var(--primary)' : 'var(--text-muted)',
                     opacity: value && HEX_RE.test(value) ? 1 : 0.4 }}>
            {isFavorite ? '★' : '☆'}
          </button>
        )}
      </div>
      {canEdit && (
        <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 4 }}>
          Saved colours are shared with everyone.
        </div>
      )}
    </div>
  );

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onMouseDown={hold}
        onClick={e => {
          e.stopPropagation();
          setHex(value || '');
          setCoords(null);          // re-measured on open, so a scrolled page still anchors correctly
          setOpen(o => !o);
        }}
        title={label}
        style={{ ...swatch(value, false, size), display: 'block' }}
      />
      {open && createPortal(panel, document.body)}
    </div>
  );
}

export { ColorPicker, PRESET_COLORS };
export default ColorPicker;
